import {
  CapsuleError,
  createQuicknetClient,
  openPrivate,
  openSenderProof,
  openPublic,
  readPublicHeader,
  readWrapHeader,
  timeForRound,
  unwrapPrivate,
  type ChainClient,
  type Recipients,
  type SenderSnapshot,
} from "@timecapsule/crypto";
import { sentListing, type Listing, type MessageRow } from "./api";
import { keyFor, oldKeys } from "./keyring";
import { openedText, sentPrivate, type Opened } from "./storage";
import { errorText, msg } from "./i18n";

let client: ChainClient | null = null;

/** One drand client for the page; encryption never touches the network, opening does. */
export function chainClient(): ChainClient {
  client ??= createQuicknetClient();
  return client;
}

export type CapsuleView = {
  id: string;
  row: MessageRow;
  isPrivate: boolean;
  /** Author pubkey; null when this device cannot tell (private capsule we cannot unwrap). */
  author: string | null;
  /** Recipient pubkey for private capsules. */
  recipient: string | null;
  /** Unlock time in ms; null when unknown on this device. */
  unlockAt: number | null;
  /** drand round of the unlock time; null when unknown on this device. */
  round: number | null;
  /** Seconds. For private capsules the wrap time is randomized, so this comes from the inner capsule when known. */
  createdAt: number;
  /** False for an unopened recipient layer or a Sent wrap without a local note/copy. */
  createdKnown?: boolean;
  /** Text and files once opened on this device (from the cache or just now); null while unopened. */
  opened: Opened | null;
  /** Set when this device cannot open the text now (not the recipient, key missing or locked). */
  unreadable: string | null;
  /** Set when the capsule is broken: bad signature, tag and ciphertext disagree, round out of range. */
  damaged: string | null;
  /** The author's profile at sending time, signed into the capsule (PLAN.md 4.10); null if absent or unknown here. */
  sender: SenderSnapshot | null;
  /** Never encrypted (PLAN.md 4.14): from the public capsule or the private wrap, known without a key. */
  subject: string | null;
  /**
   * Optional teaser readable before unlock (PLAN.md 4.17): from the public capsule, or from the
   * private one once the key unwrapped it (never on the wrap); Lock hides a private one again.
   */
  intro: string | null;
  /** The sender marked this private capsule discreet (PLAN.md 4.16 K10); known only after unwrapping. */
  discreet: boolean;
  /**
   * PLAN.md 4.18: the key that signed this private capsule is not the key the sending account had
   * (or the server gave no sender proof). Lists show "Unverified sender", never the signed name.
   */
  unverified: boolean;
  /** Lists show "Discreet sender" until the member clicks: a discreet capsule, or the member's hide_senders setting. */
  senderHidden: boolean;
  /** The author's copy of this private capsule (server field copy_of), attached by mergeCopies. */
  copy?: CapsuleView;
  /** Group send (PLAN.md 4.14): every recipient, on the one row Sent shows for it (mergeGroups). */
  recipients?: string[];
  /**
   * A private capsule this member opens with their key, which is locked (4b step 2g): who sent it,
   * when it opens and the text wait until the member opens it and gives the PIN.
   */
  needsKey: boolean;
  /** Who, when or the text came from the key: Lock hides them again (hidePrivate). */
  fromKey: boolean;
  /**
   * Reply envelope (PLAN.md 4.21), from the wrap's plaintext tag as the server lists it: the sender
   * asks for a reply opening at `unlockAt` (ms) or when the recipient chooses (null). Null: none.
   */
  sase: { unlockAt: number | null; expired: boolean } | null;
  /** The wrap this capsule answers (PLAN.md 4.21), from the wrap's plaintext tag; null otherwise. */
  replyTo: string | null;
  /** A reply the request sender's stamp paid for (server field); false otherwise. */
  stamped: boolean;
  /** CC (PLAN.md 4.21): who else received this capsule, as the sender listed them inside the seal; known after unwrapping. */
  cc: Recipients | null;
  /** The Lock count (session lockGeneration) when this view was made; content from an older one is hidden. */
  gen?: number;
};

/** The envelope of a row as the view carries it (ms). */
export function envelopeOf(row: MessageRow): CapsuleView["sase"] {
  return row.sase ? { unlockAt: row.sase.unlock === null ? null : row.sase.unlock * 1000, expired: row.sase.expired } : null;
}

export type DescribeContext = {
  listing: Listing;
  memberId: number;
  myPubkey: string | null;
  secretKey: Uint8Array | null;
  /** The key is on this device but locked: private text waits for the PIN. */
  keyLocked: boolean;
  /** The member's setting (PLAN.md 4.16 K10): hide every private sender until a click. */
  hideSenders?: boolean;
  /** Every key the member ever had, and the earlier ones the keyring holds (PLAN.md 4.16 K4). */
  myKeys?: ReadonlySet<string>;
  oldKeys?: ReadonlyMap<string, Uint8Array>;
};

/**
 * Turns a server row into what the page shows, using only local knowledge: public header,
 * the member's key for private wraps, cached text and sent-capsule notes. Never throws: a
 * broken capsule becomes a "damaged" row and the rest of the list renders (PLAN.md 4.11 H7).
 */
export async function describe(row: MessageRow, ctx: DescribeContext): Promise<CapsuleView> {
  const base = { id: row.id, row, opened: null, unreadable: null, damaged: null, sender: null, subject: null, intro: null, discreet: false, unverified: false, senderHidden: false, needsKey: false, fromKey: false, sase: envelopeOf(row), replyTo: row.reply_to ?? null, stamped: row.stamped === true, cc: null };
  try {
    return await describeOrThrow(row, ctx, base);
  } catch (err) {
    const isPrivate = row.kind === 1059;
    return { ...base, isPrivate, author: null, recipient: null, unlockAt: null, round: null, createdAt: row.created_at, damaged: explain(err) };
  }
}

async function describeOrThrow(
  row: MessageRow,
  ctx: DescribeContext,
  base: Pick<CapsuleView, "id" | "row" | "opened" | "unreadable" | "damaged" | "sender" | "subject" | "intro" | "discreet" | "unverified" | "senderHidden" | "needsKey" | "fromKey" | "sase" | "replyTo" | "stamped" | "cc">,
): Promise<CapsuleView> {
  if (row.kind === 1041) {
    const header = readPublicHeader(row.event);
    const opened = await openedText.load(ctx.memberId, row.id, null);
    return {
      ...base,
      opened,
      sender: header.sender,
      subject: header.subject,
      intro: header.intro,
      isPrivate: false,
      author: header.author,
      recipient: null,
      unlockAt: header.unlockAt,
      round: header.round,
      createdAt: header.createdAt,
      createdKnown: true,
    };
  }

  // Subject and unlock time are on the wrap in plain text (PLAN.md 4.14): known before any key is
  // used; unwrapPrivate checks them against the signed capsule.
  const wrap = readWrapHeader(row.event);
  const recipient = wrap.recipient;
  const priv = { ...base, isPrivate: true, recipient, subject: wrap.subject, createdAt: row.created_at, createdKnown: false };

  // A capsule to an earlier key of mine (K4): opened with that key from the keyring, if it is there.
  const earlier = recipient && recipient !== ctx.myPubkey && ctx.myKeys?.has(recipient) ? recipient : null;
  if (earlier && ctx.secretKey && !ctx.oldKeys?.has(earlier)) {
    return { ...priv, author: null, unlockAt: wrap.unlockAt, round: null, unreadable: msg().capsules.oldKeyMissing };
  }
  if (recipient && (recipient === ctx.myPubkey || earlier)) {
    if (!ctx.secretKey) {
      // Locked: "Open" asks for the PIN. No key on this device at all: say how to get it there.
      if (ctx.keyLocked) return { ...priv, author: null, unlockAt: wrap.unlockAt, round: null, needsKey: true };
      return { ...priv, author: null, unlockAt: wrap.unlockAt, round: null, unreadable: msg().capsules.keyMissing };
    }
    try {
      const key = earlier ? ctx.oldKeys!.get(earlier)! : ctx.secretKey;
      const h = unwrapPrivate(row.event, key);
      const opened = await openedText.load(ctx.memberId, row.id, ctx.secretKey);
      const unverified = !provenSender(row, h.author, key);
      return { ...priv, opened, sender: unverified ? null : h.sender, intro: h.intro, discreet: h.discreet, unverified, senderHidden: h.author !== ctx.myPubkey && (h.discreet || !!ctx.hideSenders), author: h.author, unlockAt: h.unlockAt, round: h.round, createdAt: h.createdAt, createdKnown: true, fromKey: true, cc: h.recipients };
    } catch (err) {
      if (err instanceof CapsuleError && err.code === "NOT_RECIPIENT") return { ...priv, author: null, unlockAt: wrap.unlockAt, round: null, unreadable: explain(err) };
      throw err;
    }
  }

  // A private capsule I sent to someone else: the wrap is encrypted to them, not to me.
  const note = sentListing(ctx.listing) ? await sentPrivate.load(ctx.memberId, row.id) : undefined;
  return {
    ...priv,
    author: ctx.myPubkey,
    unlockAt: note ? timeForRound(note.round) : wrap.unlockAt,
    round: note?.round ?? null,
    createdAt: note?.createdAt ?? row.created_at,
    createdKnown: !!note,
    unreadable: msg().capsules.noCopy,
  };
}

/**
 * PLAN.md 4.18: the server's sender proof names the key the submitting account had active; the
 * signer of the inner capsule must be that key, or the sender is unverified.
 */
function provenSender(row: MessageRow, signer: string, key: Uint8Array): boolean {
  try {
    return openSenderProof(row.sender_proof, row.id, key) === signer;
  } catch {
    return false;
  }
}

/**
 * Sent lists the wrap to the recipient and the author's copy (copy_of = wrap id, from the
 * server, so every device pairs them). Show one row: recipient from the wrap, text and unlock
 * time from the copy.
 */
export function mergeCopies(views: CapsuleView[]): CapsuleView[] {
  const copies = new Map(views.filter((v) => v.row.copy_of).map((v) => [v.row.copy_of!, v]));
  const merged = views.map((v) => {
    const copy = copies.get(v.id);
    if (!copy) return v;
    return {
      ...v,
      copy,
      opened: copy.opened,
      sender: copy.sender,
      intro: copy.intro,
      cc: copy.cc,
      unlockAt: copy.unlockAt ?? v.unlockAt,
      round: copy.round ?? v.round,
      createdAt: copy.createdKnown ? copy.createdAt : v.createdAt,
      createdKnown: copy.createdKnown || v.createdKnown,
      unreadable: copy.unreadable,
      damaged: copy.damaged,
      needsKey: copy.needsKey,
      fromKey: copy.fromKey,
    };
  });
  const paired = new Set(views.filter((v) => copies.has(v.id)).map((v) => copies.get(v.id)!.id));
  return merged.filter((v) => !paired.has(v.id));
}

/**
 * A group send is one wrap per recipient plus one copy, all with batch_of = the first wrap
 * (PLAN.md 4.14). The server lists only the first wrap, with every recipient (4.20); after
 * mergeCopies it carries the copy's text and time.
 */
export function mergeGroups(views: CapsuleView[]): CapsuleView[] {
  return views.map((v) => v.row.recipients && v.row.recipients.length > 1 ? { ...v, recipients: v.row.recipients } : v);
}

/**
 * The view as it is with the key locked (PLAN.md 4.13 R3, 4b step 2g): everything that came from
 * the key goes, text and file entries (with their keys), and for a capsule opened with the key also
 * who sent it and when it opens; the row offers "Open" again. Public capsules are public after
 * unlock and stay; a private capsule this member cannot open stays as it is.
 */
export function hidePrivate(view: CapsuleView): CapsuleView {
  if (!view.isPrivate || (!view.fromKey && view.opened === null && !view.copy?.fromKey)) return view;
  const locked = (v: CapsuleView): CapsuleView =>
    v.fromKey || v.opened !== null
      ? { ...v, opened: null, sender: null, intro: null, discreet: false, unverified: false, senderHidden: false, author: null, unlockAt: v.row.unlock_at * 1000, round: null, createdAt: v.row.created_at, createdKnown: false, needsKey: true, fromKey: false, cc: null }
      : v;
  return { ...locked(view), ...(view.copy ? { copy: locked(view.copy) } : {}) };
}

/**
 * Opens one private capsule's recipient layer with the key (4b step 2g): who, when and any text
 * this device already opened (encrypted cache). The time lock is not touched here.
 */
export async function revealWithKey(view: CapsuleView, ctx: Omit<DescribeContext, "secretKey" | "keyLocked">, key: Uint8Array): Promise<CapsuleView> {
  const full = { ...ctx, secretKey: key, keyLocked: false, oldKeys: await oldKeys(key) };
  if (!view.copy) return describe(view.row, full);
  const [main, copy] = await Promise.all([describe(view.row, full), describe(view.copy.row, full)]);
  return mergeCopies([main, copy])[0];
}

/** Fetches the drand beacon and decrypts. Throws a readable Error before the unlock time. */
export async function openCapsule(view: CapsuleView, memberId: number, secretKey: Uint8Array | null): Promise<Opened> {
  try {
    if (view.copy) return await openCapsule(view.copy, memberId, secretKey);
    let opened;
    if (!view.isPrivate) {
      opened = await openPublic(view.row.event, chainClient());
    } else {
      if (!secretKey) throw new Error(msg().errors.keyNotUnlocked);
      opened = await openPrivate(view.row.event, await keyFor(view.recipient, secretKey), chainClient());
    }
    const result = { text: opened.plaintext, files: opened.files };
    await openedText.save(memberId, view.id, result, view.isPrivate ? secretKey : null);
    return result;
  } catch (err) {
    throw new Error(explain(err));
  }
}

export function explain(err: unknown): string {
  const e = msg().errors;
  if (err instanceof CapsuleError) {
    switch (err.code) {
      case "ROUND_NOT_REACHED":
        return e.notYet;
      case "BEACON_UNAVAILABLE":
        return e.beacon;
      case "NOT_RECIPIENT":
        return e.notRecipient;
      case "INVALID_SIGNATURE":
        return e.signature;
      default:
        return e.damaged(err.message);
    }
  }
  return errorText(err);
}
